import { fitFontSize } from '../../utils/textFit';
import React, { useState, useEffect, useRef } from 'react';
import { Search, Printer, Users, Eye, X, AlertCircle, Settings, Upload, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/api/api';

// Standard I-Card Size (CR80): 85.6mm x 54mm
// For screen preview, we scale by ~3.78x
// Portrait orientation: 54mm width x 86mm height = 204px x 324px
const CARD_WIDTH = 204;  // 54mm scaled
const CARD_HEIGHT = 324; // 86mm scaled

// Paper size options with card capacity
const PAPER_SIZES = [
  { id: 'a4', name: 'A4', width: '210mm', height: '297mm', cardsPerPage: 8, columns: 2, description: '8 cards/page (2×4)' },
  { id: 'a3', name: 'A3', width: '297mm', height: '420mm', cardsPerPage: 15, columns: 3, description: '15 cards/page (3×5)' },
  { id: 'cr80', name: 'CR80 (I-Card Machine)', width: '54mm', height: '85.6mm', cardsPerPage: 1, columns: 1, description: '1 card - Standard Size (54×86mm)' }
];

const PrintICard = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [paperSize, setPaperSize] = useState('a4');
  const [printSide, setPrintSide] = useState('both'); // 'front', 'back', 'both'
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // student id being uploaded
  const printRef = useRef();
  const fileInputRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch students and template in parallel
      const [studentsRes, templateRes] = await Promise.all([
        api.get('/students/dropdown'),
        api.get('/icard-builder/template')
      ]);
      
      setStudents(studentsRes.data.students || studentsRes.data || []);
      setTemplate(templateRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get student photo from various sources
  const getStudentPhoto = (student) => {
    // Helper to extract data URI from field (could be string or {data, filename, mimetype} object)
    const extract = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && val.length > 10) return val;
      if (typeof val === 'object' && val.data) return val.data;
      return null;
    };
    
    // Direct fields
    const direct = extract(student.photo) || extract(student.passport_photo) || extract(student.photo_url) || extract(student.profile_photo);
    if (direct) return direct;
    
    // Embedded documents object
    if (student.documents && typeof student.documents === 'object' && !Array.isArray(student.documents)) {
      for (const key of ['passport_photo', 'photo', 'profile_photo', 'passport_size_photo', 'student_photo']) {
        const val = extract(student.documents[key]);
        if (val) return val;
      }
    }
    
    // Documents array
    if (Array.isArray(student.documents)) {
      const photoDoc = student.documents.find(d => 
        ['photo', 'profile_photo', 'passport_photo', 'passport_size_photo', 'student_photo'].includes(d.type?.toLowerCase())
      );
      if (photoDoc) return extract(photoDoc) || photoDoc.url;
    }
    
    return null;
  };

  // Handle photo upload
  const handlePhotoUpload = async (studentId, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload image files only');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo size cannot exceed 2MB');
      return;
    }
    
    setUploadingPhoto(studentId);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        try {
          // Upload to backend
          await api.put(`/students/${studentId}/photo`, {
            photo: base64Data
          });
          
          // Update local state
          setStudents(prev => prev.map(s => 
            s.id === studentId ? { ...s, photo: base64Data } : s
          ));
          
          toast.success('Photo uploaded successfully');
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast.error('Failed to upload photo');
        } finally {
          setUploadingPhoto(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read file');
      setUploadingPhoto(null);
    }
  };

  const filteredStudents = students.filter(student =>
    student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.course_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const generateICards = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    if (!template || !template.front_fields || template.front_fields.length === 0) {
      toast.error('No I-Card Template found. Please create a template in I-Card Builder first.');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/icard-builder/generate-batch', {
        student_ids: selectedStudents
      });
      setPrintData(response.data);
      toast.success(`${response.data.cards.length} I-Cards generated!`);
    } catch (error) {
      toast.error('Failed to generate I-Cards');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = (side = printSide) => {
    const printContent = printRef.current;
    const selectedPaper = PAPER_SIZES.find(p => p.id === paperSize) || PAPER_SIZES[0];
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site, then click Print again.');
      return;
    }
    
    const cardPairs = printContent.querySelectorAll('.card-pair');
    const isCR80 = paperSize === 'cr80';
    
    let contentHtml = '';
    
    if (isCR80) {
      // CR80: Each card on separate page (front then back)
      cardPairs.forEach(pair => {
        if (side === 'front' || side === 'both') {
          const frontCard = pair.querySelector('[data-side="front"] .card-content');
          if (frontCard) contentHtml += `<div class="card-single">${frontCard.outerHTML}</div>`;
        }
        if (side === 'back' || side === 'both') {
          const backCard = pair.querySelector('[data-side="back"] .card-content');
          if (backCard) contentHtml += `<div class="card-single">${backCard.outerHTML}</div>`;
        }
      });
    } else {
      // A4/A3: All fronts on one page, all backs on next page (for duplex printing)
      let frontCards = '';
      let backCards = '';
      
      cardPairs.forEach(pair => {
        const frontCard = pair.querySelector('[data-side="front"] .card-content');
        const backCard = pair.querySelector('[data-side="back"] .card-content');
        
        if (frontCard) frontCards += `<div class="card-item">${frontCard.outerHTML}</div>`;
        if (backCard) backCards += `<div class="card-item">${backCard.outerHTML}</div>`;
      });
      
      if (side === 'front') {
        contentHtml = `<div class="page-grid">${frontCards}</div>`;
      } else if (side === 'back') {
        contentHtml = `<div class="page-grid">${backCards}</div>`;
      } else {
        // Both sides: Front page first, then Back page (for duplex/back-to-back printing)
        contentHtml = `
          <div class="page-grid front-page">${frontCards}</div>
          <div class="page-break"></div>
          <div class="page-grid back-page">${backCards}</div>
        `;
      }
    }

    const gridColumns = selectedPaper.columns || 2;
    
    // Fix relative image URLs (popup window has no origin, so /uploads/... breaks)
    contentHtml = contentHtml.replace(/src="\//g, `src="${window.location.origin}/`);
    
    // Actual I-Card size for printing: 54mm x 86mm (Portrait)
    const cardPrintWidth = '54mm';
    const cardPrintHeight = '86mm';
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print I-Cards - ${side === 'front' ? 'Front Side' : side === 'back' ? 'Back Side' : 'Both Sides (Duplex)'}</title>
          <style>
            @page { 
              size: ${selectedPaper.width} ${selectedPaper.height}; 
              margin: ${isCR80 ? '0' : '5mm'}; 
            }
            * { box-sizing: border-box; }
            body { 
              margin: 0; 
              padding: 0; 
              font-family: system-ui, -apple-system, sans-serif; 
            }
            
            /* Actual I-Card size */
            .card-content {
              width: ${cardPrintWidth} !important;
              height: ${cardPrintHeight} !important;
              overflow: hidden;
              position: relative;
              background-size: cover !important;
              background-position: center !important;
            }
            
            /* Field positioning & media (Tailwind classes are not available in this window) */
            .card-content > div { position: absolute; }
            .card-content img { width: 100%; height: 100%; object-fit: cover; }
            .card-content img.qr-img { object-fit: contain !important; image-rendering: pixelated; background: #fff; }
            .rounded-full { border-radius: 50%; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            .text-left { text-align: left; }
            .w-full { width: 100%; }
            .bg-gray-200 { background: #e5e7eb; }
            .object-contain { object-fit: contain !important; }
            .overflow-hidden { overflow: hidden; }
            
            /* Grid layout for A4/A3 */
            .page-grid {
              display: grid;
              grid-template-columns: repeat(${gridColumns}, ${cardPrintWidth});
              gap: 5mm;
              padding: 5mm;
              justify-content: center;
              align-items: start;
            }
            
            .card-item {
              page-break-inside: avoid;
            }
            
            /* Page break between front and back */
            .page-break {
              page-break-after: always;
              height: 0;
            }
            
            /* CR80 single card per page */
            .card-single {
              page-break-after: always;
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 0;
            }
            .card-single:last-child {
              page-break-after: auto;
            }
            
            .side-label { display: none !important; }
            
            @media print {
              .page-break { page-break-after: always; }
              .card-item { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>${contentHtml}</body>
      </html>
    `);
    printWindow.document.close();
    
    // Record print when user actually prints
    printWindow.onafterprint = async () => {
      try {
        const studentIds = printData.cards.map(c => c.student.id);
        await api.post('/icard-builder/record-print', {
          student_ids: studentIds
        });
        toast.success(`Print recorded for ${studentIds.length} I-Cards`);
      } catch (error) {
        console.error('Error recording print:', error);
      }
    };
    
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 1500);
    }, 900);
  };

  // Get contact info string
  const getContactInfo = () => {
    if (!template) return '';
    const parts = [];
    if (template.college_email) parts.push(template.college_email);
    if (template.college_website) parts.push(template.college_website);
    if (template.college_phone) parts.push(template.college_phone);
    return parts.join(' | ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if template exists
  const hasTemplate = template && template.front_fields && template.front_fields.length > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Printer className="h-7 w-7 text-blue-600" />
            Print I-Card
          </h1>
          <p className="text-gray-600 mt-1">Select students to print I-Cards</p>
        </div>
        
        <Link
          to="/admission/icard-builder"
          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
        >
          <Settings className="h-4 w-4" />
          Template Settings
        </Link>
      </div>

      {/* No Template Warning */}
      {!hasTemplate && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-yellow-800">No I-Card Template Found!</h3>
            <p className="text-yellow-700 text-sm mt-1">
              You need to create a template before printing I-Cards.
            </p>
            <Link
              to="/admission/icard-builder"
              className="inline-flex items-center gap-1 mt-2 text-sm text-yellow-800 font-medium hover:underline"
            >
              <Settings className="h-4 w-4" />
              Go to I-Card Builder to create a Template →
            </Link>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!printData ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Search & Actions */}
          <div className="p-4 border-b bg-gray-50 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search students (name, ID, Course)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-input"
              />
            </div>
            
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              data-testid="select-all-btn"
            >
              {selectedStudents.length === filteredStudents.length && filteredStudents.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>

          {/* Students List */}
          <div className="max-h-[500px] overflow-y-auto">
            {filteredStudents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No students found</p>
              </div>
            ) : (
              filteredStudents.map(student => {
                const studentPhoto = getStudentPhoto(student);
                return (
                <div
                  key={student.id}
                  className={`flex items-center gap-4 p-4 border-b transition-colors ${
                    selectedStudents.includes(student.id) ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`student-${student.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => handleSelectStudent(student.id)}
                    className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  
                  {/* Photo or Upload */}
                  <div className="relative w-14 h-14 flex-shrink-0">
                    {studentPhoto ? (
                      <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-gray-200">
                        <img src={studentPhoto} className="w-full h-full object-cover" alt="" />
                      </div>
                    ) : (
                      <label className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-400 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(student.id, e.target.files[0])}
                          disabled={uploadingPhoto === student.id}
                        />
                        {uploadingPhoto === student.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        ) : (
                          <>
                            <Camera className="w-5 h-5 text-gray-400" />
                            <span className="text-[8px] text-gray-500 mt-0.5">Upload</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{student.full_name}</p>
                    <p className="text-sm text-gray-500">
                      {student.admission_number} • {student.course_name} • {student.session}
                    </p>
                    {!studentPhoto && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ No photo - Upload required</p>
                    )}
                  </div>
                  
                  {student.blood_group && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      {student.blood_group}
                    </span>
                  )}
                </div>
              )})
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-blue-600">{selectedStudents.length}</span> students selected
            </div>
            
            <button
              onClick={generateICards}
              disabled={selectedStudents.length === 0 || !hasTemplate || generating}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Generate I-Cards
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Print Preview */
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Preview Header */}
          <div className="p-4 border-b bg-green-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-green-800">
                  ✅ {printData.cards.length} I-Cards Generated!
                </h2>
                <p className="text-sm text-green-600">Use the buttons below to print</p>
              </div>
              
              <button
                onClick={() => setPrintData(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ← Back
              </button>
            </div>
            
            {/* Print Settings */}
            <div className="flex flex-wrap items-center gap-4 p-4 bg-white rounded-lg border">
              {/* Paper Size */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Paper Size:</label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="paper-size-select"
                >
                  {PAPER_SIZES.map(size => (
                    <option key={size.id} value={size.id}>
                      {size.name} - {size.description}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Print Side */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Print:</label>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPrintSide('front')}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      printSide === 'front' 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid="print-front-btn"
                  >
                    Front Only
                  </button>
                  <button
                    onClick={() => setPrintSide('back')}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      printSide === 'back' 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid="print-back-btn"
                  >
                    Back Only
                  </button>
                  <button
                    onClick={() => setPrintSide('both')}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      printSide === 'both' 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid="print-both-btn"
                  >
                    Both (Duplex)
                  </button>
                </div>
              </div>
              
              {/* Print Button */}
              <button
                onClick={() => handlePrint()}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium ml-auto"
                data-testid="print-btn"
              >
                <Printer className="h-4 w-4" />
                Print {printSide === 'front' ? 'Front Only' : printSide === 'back' ? 'Back Only' : '(Front+Back)'}
              </button>
            </div>
            
            {/* Duplex Print Instructions */}
            {printSide === 'both' && paperSize !== 'cr80' && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>💡 Duplex Print Tip:</strong> Page 1 will print all Front sides and Page 2 will print all Back sides. 
                Select "Flip on Long Edge" / "Back-to-Back" option in your printer settings for proper card alignment.
              </div>
            )}
          </div>

          {/* Cards Preview */}
          <div className="p-6 bg-gray-100 overflow-auto max-h-[70vh]" ref={printRef}>
            <div className="space-y-8">
              {printData.cards.map((card, index) => (
                <div key={index} className="card-pair flex gap-6 justify-center bg-white p-6 rounded-lg shadow-md">
                  {/* Front Side */}
                  <div data-side="front" className="card-wrapper">
                    <p className="text-center text-xs text-gray-500 mb-2 side-label">Front Side</p>
                    <div 
                      className="card-content relative bg-white shadow-lg"
                      style={{
                        width: `${CARD_WIDTH}px`,
                        height: `${CARD_HEIGHT}px`,
                        backgroundImage: template.front_background ? `url(${template.front_background})` : 'none',
                        backgroundSize: 'cover',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      {template.front_fields?.filter(f => f.visible).map(field => {
                        const isRound = field.shape === 'round';
                        const isMedia = ['photo', 'logo', 'qr_code', 'qr', 'signature'].includes(field.id);
                        const headerTexts = {
                          college_name: template.college_name,
                          college_address: template.college_address,
                          college_contact: getContactInfo()
                        };
                        const isHeaderText = field.id in headerTexts;
                        return (
                          <div
                            key={field.id}
                            className={`absolute ${isMedia ? 'overflow-hidden' : ''}`}
                            style={{
                              left: `${field.x}px`,
                              top: `${field.y}px`,
                              width: `${field.width}px`,
                              height: isMedia ? `${field.height}px` : 'auto',
                              minHeight: `${field.height}px`,
                              fontSize: isHeaderText ? `${fitFontSize(headerTexts[field.id], field.fontSize, field.width, field.height)}px` : `${field.fontSize}px`,
                              color: field.color,
                              fontWeight: field.fontWeight || 'normal',
                              textAlign: field.textAlign || 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: field.textAlign === 'center' ? 'center' : field.textAlign === 'right' ? 'flex-end' : 'flex-start',
                              lineHeight: 1.15,
                              borderRadius: isRound ? '50%' : '0'
                            }}
                          >
                            {field.id === 'photo' && (
                              card.student.photo ? (
                                <img src={card.student.photo} className={`w-full h-full object-cover ${isRound ? 'rounded-full' : ''}`} alt="" />
                              ) : (
                                <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
                                  <Users className="w-1/2 h-1/2 text-gray-400" />
                                </div>
                              )
                            )}
                            {field.id === 'logo' && (
                              template.college_logo ? (
                                <img src={template.college_logo} className={`w-full h-full object-cover ${isRound ? 'rounded-full' : ''}`} alt="" />
                              ) : (
                                <div className={`w-full h-full bg-purple-100 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
                                  <span className="text-lg">🏛️</span>
                                </div>
                              )
                            )}
                            {field.id === 'college_name' && (template.college_name || 'College Name')}
                            {field.id === 'college_address' && (template.college_address || '')}
                            {field.id === 'college_contact' && getContactInfo()}
                            {field.id === 'name' && card.student.name}
                            {field.id === 'course' && card.student.course}
                            {field.id === 'student_no' && card.student.student_no}
                            {field.id === 'session' && card.student.session}
                            {field.id === 'admission_date' && card.student.admission_date}
                            {field.id === 'blood_group' && card.student.blood_group}
                            {field.id === 'phone' && card.student.phone}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Back Side */}
                  <div data-side="back" className="card-wrapper">
                    <p className="text-center text-xs text-gray-500 mb-2 side-label">Back Side</p>
                    <div 
                      className="card-content relative bg-white shadow-lg"
                      style={{
                        width: `${CARD_WIDTH}px`,
                        height: `${CARD_HEIGHT}px`,
                        backgroundImage: template.back_background ? `url(${template.back_background})` : 'none',
                        backgroundSize: 'cover',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      {template.back_fields?.filter(f => f.visible).map(field => {
                        const isMedia = ['photo', 'logo', 'qr_code', 'qr', 'signature'].includes(field.id);
                        const isTextarea = field.type === 'textarea';
                        return (
                        <div
                          key={field.id}
                          className={`absolute ${(isMedia || isTextarea) ? 'overflow-hidden' : ''}`}
                          style={{
                            left: `${field.x}px`,
                            top: `${field.y}px`,
                            width: `${field.width}px`,
                            height: (isMedia || isTextarea) ? `${field.height}px` : 'auto',
                            minHeight: `${field.height}px`,
                            fontSize: `${field.fontSize}px`,
                            color: field.color,
                            fontWeight: field.fontWeight || 'normal',
                            textAlign: field.textAlign || 'left',
                            display: 'flex',
                            alignItems: isTextarea ? 'flex-start' : 'center',
                            justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start',
                            lineHeight: isTextarea ? '1.5' : 1.15
                          }}
                        >
                          {field.id === 'terms_title' && (template.terms_title || 'TERMS & CONDITIONS')}
                          {field.id === 'terms' && (
                            <div className="whitespace-pre-wrap text-left w-full">{template.terms_text}</div>
                          )}
                          {field.id === 'student_no_back' && card.student.student_no}
                          {field.id === 'qr_code' && card.qr_code && (
                            <img src={card.qr_code} className="w-full h-full qr-img" style={{ objectFit: 'contain', imageRendering: 'pixelated', background: '#fff' }} alt="QR" />
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintICard;
